import ThreeDRotation from '@assets/images/itinirary_icons/3d-rotate.png'
import BackToLocation from '@assets/images/itinirary_icons/back_to_location.svg'
import Highlight from '@assets/images/itinirary_icons/poi.svg'
import ReturnNorth from '@assets/images/itinirary_icons/Return North.svg'
import Timezones from '@assets/images/itinirary_icons/timezone.svg'
import PressureLayer from '@assets/images/windy_icons/baro pressure.svg'
import CloudsLayer from '@assets/images/windy_icons/clouds.svg'
import RainLayer from '@assets/images/windy_icons/Humidity.svg'
import TidesLayer from '@assets/images/windy_icons/sea.svg'
import TemperatureLayer from '@assets/images/windy_icons/temperature.svg'
import WindLayer from '@assets/images/windy_icons/wind.svg'
import { AppConfig } from '@config'
import { setYachtTransformation } from '@constants/utils'
import type { MarkerCoords } from '@containers/admin/types'
import Logger from '@logger'
import type { ILatLng, ILatLon } from '@shared_types/global_types'
import { WindyLayers } from '@shared_types/map_types'
import { isNonEmptyArray } from '@shared_utils/array_utils'
import { INIT_ZOOM } from '@store/reducers/mapConfig'
import { degreesToRadians } from '@turf/helpers'
import { getLatLngFromTrackPoints } from '@utils/geo_utils'
import { isValidGeolocation, isValidLatitude, isValidLongitude } from 'is-valid-geolocation'
import type { LatLngLiteral } from 'leaflet'
import type { LngLatLike, Map, Marker } from 'mapbox-gl'

import { createMapboxMarker, createMercatorCoordinateFromLngLat } from './Mapbox/mapbox_helpers'
import type { MarkersPositionsAndCoords } from './useMapboxMap'

const logger = Logger.create('MapOptions')

export interface IMapOptions extends ILatLon {
  key: string
  zoom: number
  container: string
}

export const options: IMapOptions = {
  key: '9YAjOwJPwnmEVkm0bDAC8M5CwOW7YIVA', // REPLACE WITH YOUR KEY!
  lat: 42.4,
  lon: 5.3,
  zoom: INIT_ZOOM,
  container: '#map'
}

export interface IMapLayerItem {
  type: string
  img: string
  key: string
  hint: string
  show: boolean
}

interface AllMapLayers {
  showInMenu: boolean
  items: (IMapLayerItem | string)[]
}

type I2 = Record<string, AllMapLayers>

export const allMapLayers: I2[] = [
  {
    windy: {
      showInMenu: true,
      items: [
        { type: 'wind', img: WindLayer, key: 'wind', hint: 'Wind', show: true },
        {
          type: 'temperature',
          img: TemperatureLayer,
          key: 'temp',
          hint: 'Temperature',
          show: true
        },
        { type: 'rain', img: RainLayer, key: 'rain', hint: 'Rain', show: true },
        {
          type: 'pressure',
          img: PressureLayer,
          key: 'pressure',
          hint: 'Pressure',
          show: true
        },
        {
          type: 'clouds',
          img: CloudsLayer,
          key: 'clouds',
          hint: 'Clouds',
          show: true
        },
        {
          type: 'Waves',
          img: TidesLayer,
          key: 'waves',
          hint: 'Waves',
          show: true
        }
      ]
    }
  },
  {
    mapbox2d: {
      showInMenu: false,
      items: ['']
    },
    mapbox3d: {
      showInMenu: false,
      items: ['']
    }
  }
]

export const routeOptions = {
  id: 'route',
  type: 'line',
  source: 'route',
  layout: {
    'line-join': 'round',
    'line-cap': 'round'
  },
  paint: {
    'line-color': '#fff',
    'line-width': 1,
    'line-dasharray': [4, 4]
  }
}

export interface Nav {
  type: string
  content: string
  id: string
  key?: string
  // not used params
  // items?: any[]
  hide?: string
  hint: string
}

export interface RightNav extends Nav {
  to: string
  disabled?: boolean
}

export const leftNav: Nav[] = [
  {
    type: 'center',
    content: BackToLocation,
    id: 's',
    hint: 'Back to location'
  },
  {
    type: 'showNorth',
    content: ReturnNorth,
    id: 'f',
    hint: 'Back to north',
    hide: 'windy',
    key: 'north'
  },
  {
    type: 'dropdown',
    content: 'img',
    id: 'mapboxLayers',
    hide: 'windy',
    hint: ''
  }
]

export type LayerData = {
  values: string[]
  colors: string[]
}

const windyLayerData: Record<WindyLayers, LayerData> = {
  [WindyLayers.CLOUDS]: {
    values: [],
    colors: []
  },
  [WindyLayers.WAVES]: {
    values: [],
    colors: []
  },
  [WindyLayers.TEMPERATURE]: {
    values: ['40', '30', '20', '10', '0', '-10', '-20', '-'],
    colors: [
      'rgb(138, 43, 10)',
      'rgb(138, 43, 10)',
      'rgb(190, 65, 18)',
      'rgb(236, 95, 21)',
      'rgb(243, 150, 6)',
      'rgb(223, 177, 6)',
      'rgb(171, 161, 14)',
      'rgb(121, 146, 28)',
      'rgb(80, 140, 62)',
      'rgb(95, 143, 197)',
      'rgb(103, 180, 186)',
      'rgb(129, 204, 197)',
      'rgb(150, 209, 216)',
      'rgb(149, 137, 211)',
      'rgb(149, 137, 211)',
      'rgb(149, 137, 211)',
      'rgb(149, 137, 211)'
    ]
  },
  [WindyLayers.WIND]: {
    values: ['60', '40', '30', '20', '10', '5', '0', '-'],
    colors: [
      'rgb(91, 136, 161)',
      'rgb(91, 136, 161)',
      'rgb(95, 100, 160)',
      'rgb(151, 75, 145)',
      'rgb(141, 63, 92)',
      'rgb(162, 109, 92)',
      'rgb(162, 135, 64)',
      'rgb(103, 164, 54)',
      'rgb(76, 164, 76)',
      'rgb(77, 142, 124)',
      'rgb(74, 146, 148)',
      'rgb(74, 148, 170)',
      'rgb(61, 110, 163)',
      'rgb(98, 113, 184)',
      'rgb(98, 113, 184)',
      'rgb(98, 113, 184)',
      'rgb(98, 113, 184)'
    ]
  },
  [WindyLayers.RAIN]: {
    values: ['30', '20', '10', '7', '3', '2', '1.5', 'mm'],
    colors: [
      'rgb(164, 58, 154)',
      'rgb(164, 58, 154)',
      'rgb(171, 54, 107)',
      'rgb(162, 60, 59)',
      'rgb(162, 162, 59)',
      'rgb(129, 162, 59)',
      'rgb(74, 164, 57)',
      'rgb(50, 166, 111)',
      'rgb(58, 153, 162)',
      'rgb(58, 137, 162)',
      'rgb(58, 133, 162)',
      'rgb(59, 128, 162)',
      'rgb(59, 126, 162)',
      'rgb(59, 124, 162)',
      'rgb(59, 124, 162)',
      'rgb(59, 124, 162)',
      'rgb(59, 124, 162)'
    ]
  },
  // [WindyLayers.PRESSURE]: {
  //   values: ['1030', '1020', '1010', '1000', '990', 'hPa'],
  //   colors: [
  //     'rgb(160, 82, 44)',
  //     'rgb(160, 82, 44)',
  //     'rgb(163, 111, 63)',
  //     'rgb(167, 142, 99)',
  //     'rgb(178, 176, 157)',
  //     'rgb(140, 178, 168)',
  //     'rgb(72, 154, 152)',
  //     'rgb(18, 134, 147)',
  //     'rgb(0, 117, 147)',
  //     'rgb(0, 103, 148)',
  //     'rgb(0, 103, 148)',
  //     'rgb(0, 103, 148)',
  //     'rgb(0, 103, 148)'
  //   ]
  // },
  [WindyLayers.PRESSURE]: {
    values: ['9', '6', '2', '1.5', '1', '0.5', 'm'],
    colors: [
      'rgb(192, 162, 157)',
      'rgb(192, 162, 157)',
      'rgb(192, 118, 105)',
      'rgb(194, 76, 91)',
      'rgb(134, 48, 49)',
      'rgb(57, 61, 143)',
      'rgb(56, 83, 169)',
      'rgb(56, 104, 192)',
      'rgb(52, 101, 166)',
      'rgb(48, 99, 142)',
      'rgb(48, 128, 164)',
      'rgb(50, 158, 186)',
      'rgb(50, 158, 186)',
      'rgb(50, 158, 186)',
      'rgb(50, 158, 186)'
    ]
  }
}

export const getBarValues = (layer: WindyLayers): LayerData => windyLayerData[layer]

export interface MapboxDropdownOption {
  type: string
  img: string
  hint: string
  secondHint: string
  thirdHint: string
}

export interface MapboxDropdownOptions {
  mapbox2d: MapboxDropdownOption[]
  mapbox3d: MapboxDropdownOption[]
}

export const moreOptions: MapboxDropdownOptions = {
  mapbox2d: [
    {
      type: 'highlights',
      img: Highlight,
      hint: 'Show highlights',
      secondHint: 'Hide highlights',
      thirdHint: 'No highlight to show'
    },
    {
      type: 'timezones',
      img: Timezones,
      hint: 'Show timezones',
      secondHint: 'Hide timezones',
      thirdHint: 'No timezones to show'
    }
    // { type: 'tenders', img: Tenders, hint: 'Show tenders', secondHint: 'Hide tenders', thirdHint: 'No tenders to show' }
    // { type: 'AIS', img: AIS, hint: 'Show AIS', secondHint: 'Hide AIS', thirdHint: 'No AIS to show' }
  ],
  mapbox3d: [
    {
      type: 'rotate3d',
      img: ThreeDRotation,
      hint: 'Turn on 3D rotation',
      secondHint: 'Turn off 3D rotation',
      thirdHint: ''
    }
  ]
}

export const drawShip = (
  map: Map | null,
  pos: ILatLng,
  heading: number,
  yachtMarker: Marker | null,
  boatIcon?: string,
  yachtId?: string
) => {
  if (!map) {
    logger.verbose('drawShip: no map reference')
    return null
  }

  if (!isValidGeolocation(pos)) {
    logger.verbose(`drawShip: invalid position=${pos}`)
    return null
  }

  const point: LngLatLike | [] = [pos.lng, pos.lat]

  const div = document.createElement('div')

  // to prevent linter warnings
  const rotate = `${-heading}deg`

  div.className = 'yacht-marker-wrapper'
  div.innerHTML = `<div class="${yachtId || 'yacht-marker'}" data-markertype="current-location" style="transform: rotate(${rotate})">
      <span data-markertype="current-location"></span>
      <span data-markertype="current-location"></span>
      <span data-markertype="current-location"></span>
    </div>`

  if (yachtMarker) {
    yachtMarker.remove()
  }
  yachtMarker = createMapboxMarker(div, {
    pitchAlignment: 'map',
    rotationAlignment: 'viewport'
  })
    .setLngLat(point)
    .addTo(map)

  setYachtTransformation(map, heading, boatIcon, undefined, yachtId)

  return yachtMarker
}

export function getPositionOfPointOnRoute (point: number[], route: number[][]) {
  if (isNonEmptyArray(point, 1) && route && Array.isArray(route)) {
    const [lng, lat] = point
    if (!isValidLatitude(lat) || !isValidLongitude(lng)) {
      return
    }

    for (let i = 0; i < route.length - 1; i++) {
      const pA = { x: route[i][1], y: route[i][0] }
      const pB = { x: route[i + 1][1], y: route[i + 1][0] }

      const res = pA.x * pB.y + pB.x * lng + lat * pA.y - pB.x * pA.y - pA.x * lng - lat * pB.y

      if (res < 0.5 && res > -0.5) {
        return { position: ++i, value: route[i] }
      }
    }
  }

  return
}

export const getDataFromGpx = (gpx: string) => {
  const parser = new DOMParser()
  const xmlDoc: Document = parser.parseFromString(gpx, 'text/xml')

  const points = Array.from(xmlDoc.getElementsByTagName('trkpt'))
  const waypoints = Array.from(xmlDoc.getElementsByTagName('wpt'))

  let data: MarkersPositionsAndCoords | undefined

  if (points.length) {
    const latlngs = getLatLngFromTrackPoints(points)
    const markerCoords: MarkerCoords = {
      latStart: latlngs[0].lat,
      lngStart: latlngs[0].lng,
      latEnd: latlngs[latlngs.length - 1].lat,
      lngEnd: latlngs[latlngs.length - 1].lng
    }

    data = { latlngs, markerCoords }
  }

  return { data, xmlDoc, waypoints }
}

// for mapbox map
export const updateSource = (map: Map, key: string, data: LngLatLike[]) => {
  if (!map) {
    console.trace('updateSource not possible, no map instance given')
    return
  }

  try {
    if (map.getSource(key)) {
      // @ts-expect-error src not contain setData function
      map.getSource(key).setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: data
        }
      })
    }
  } catch (e) {
    console.log(`cannot update source with key=${key}: ${e}`)
  }
}

const isDirectionChanged = (initialValue: number, currentValue: number, margin: number) => {
  return initialValue - margin >= currentValue || initialValue + margin <= currentValue
}

export class Camera {
  follow?: LatLngLiteral | null
  altitude: number
  speedY: number
  speedX: number
  rotationSpeed: number
  margin: number
  itinerary?: LatLngLiteral[]
  map: Map
  point: LatLngLiteral = { lat: 12, lng: 42 }
  top: number = 0
  left: number = 0
  rotation: number = 0

  constructor (
    follow: LatLngLiteral | null | undefined,
    altitude: number,
    speedY: number,
    speedX: number,
    rotationSpeed: number,
    margin: number,
    map: Map,
    itinerary?: LatLngLiteral[]
  ) {
    this.follow = follow
    this.altitude = altitude
    this.speedY = speedY
    this.speedX = speedX
    this.rotationSpeed = rotationSpeed
    this.margin = margin
    this.itinerary = itinerary
    this.map = map
  }

  setup () {
    this.point
      = this.follow
      || (this.itinerary && this.itinerary.length
        ? this.itinerary[this.itinerary.length - 1]
        : {
          lat: 12,
          lng: 42
        })
    this.top = this.point.lat - this.margin
    this.left = this.point.lng - this.margin
    this.rotation = 0
  }

  update () {
    this.updatePosition([this.left, this.top], this.altitude, this.point)
    if (isDirectionChanged(this.point.lat, this.top, this.margin)) {
      this.speedY = -this.speedY
    }
    if (isDirectionChanged(this.point.lng, this.left, this.margin)) {
      this.speedX = -this.speedX
    }
    this.top -= this.speedY
    this.left -= this.speedX
    this.rotation += this.rotationSpeed
    this.map.setBearing(this.rotation)
  }

  updatePosition (position: LngLatLike, altitude: number, target: LatLngLiteral) {
    const camera = this.map.getFreeCameraOptions()

    camera.position = createMercatorCoordinateFromLngLat(position, altitude)
    camera.lookAtPoint([target.lng, target.lat])
    this.map.setFreeCameraOptions(camera)
  }
}

export const drawSpanWithTransformation = (
  x: number,
  y: number,
  i: number,
  marker: HTMLElement,
  className?: string
) => {
  const span = document.createElement('span')
  span.style.transform = `translate(calc(${x}px - 50%), calc(${y}px - 50%)) rotate(${i * 45 + 90}deg)`
  className && span.classList.add(className)
  marker.appendChild(span)
}

export const circularMove = (radius: number, deg: number, step: number) => ({
  x: radius * Math.cos(degreesToRadians(deg * step)),
  y: radius * Math.sin(degreesToRadians(deg * step))
})

export const drawTextWithTransformation = (
  x: number,
  y: number,
  i: number,
  marker: HTMLElement,
  className?: string
) => {
  const div = document.createElement('div')
  const compassText = ['E', 'S', 'W', 'N']
  div.style.transform = `translate(calc(${x}px - 50%), calc(${y}px - 50%))`
  div.textContent = compassText[i]
  className && div.classList.add(className)
  marker.appendChild(div)
}

export const setDropDownPosition = (el: HTMLElement) => {
  const menu: HTMLElement | null = document.querySelector('.layer-container')
  const item: HTMLElement | null = document.querySelector('.nav-item')

  if (menu && item && el) {
    menu.style.left = `${el.getBoundingClientRect().left - menu?.offsetWidth + item?.offsetWidth}px`
  }
}

export const setBarLeft = async () => {
  const barEl: HTMLElement | null = document.querySelector('.bar')
  const barBtn: HTMLElement | null = document.querySelector('.bar__btn')

  if (barEl) {
    const barMeasurements = barEl.getBoundingClientRect()

    if (!barEl.querySelector('.display-contents canvas')) {
      setTimeout(setBarLeft, 20)
    }
    barEl.style.left = -barMeasurements.width + 'px'
    barBtn && (barBtn.style.left = barMeasurements.width + 'px')
  }
}

// check if windy has to be disabled or enabled - offline mode
export const checkIfWindyIsEnabled = () => AppConfig.weatherMaps.enabled()
